/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { SplitText } from 'gsap/SplitText';
import React, { useRef } from 'react';
import backClouds from '../../images/hero_cloud_back.png';
import frontClouds from '../../images/hero_cloud_front.png';
import topCloud from '../../images/hero_cloud_top.png';
import table from '../../images/hero_table.png';
import { BackgroundColorTheme } from '../../templates/Page';
import { blendEases } from '../../utils/gsap';
import { usePersistentState } from '../../utils/hooks';
import { clsx } from '../../utils/utils';
import * as styles from './HomepageHero.module.scss';
import ScrollingItems from './ScrollingItems';

const TABLE_IMAGE_WIDTH_PX = 1140;

export type HeroType = {
  title: string;
};

type HeroProps = HeroType & {
  theme?: BackgroundColorTheme;
  className?: string;
};

const HomepageHero = ({ title, className }: HeroProps): React.ReactElement => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [hasLoadedBefore, setHasLoadedBefore] = usePersistentState<boolean>(
    'hasLoadedBefore',
    false,
    'session',
  );

  useGSAP(
    () => {
      // HACK: Somehow fixes jumping table bug ¯\_(ツ)_/¯
      window.scrollBy({ top: 1 });

      const mm = gsap.matchMedia();

      mm.add(
        {
          isDesktop: '(min-width: 1200px)',
          isTablet: '(min-width: 720px) and (max-width: 1199px)',
          isMobile: '(max-width: 719px)',
          isTallScreen: '(min-height: 768px)',
          isShortScreen: '(max-height: 767px)',
        },
        context => {
          const { isTallScreen, isDesktop } = context.conditions || {};

          const titleSplit = new SplitText(`.${styles.title}`, { type: 'words' });
          const tl = gsap
            .timeline()
            .set(`.${styles.title}`, {
              autoAlpha: 1,
            })
            .set(`.${styles.table}`, {
              autoAlpha: 1,
              x: isDesktop ? '-50%' : '0%',
            })
            .from(titleSplit.words, {
              delay: 0.5,
              y: 20,
              autoAlpha: 0,
              duration: 1,
              stagger: 1.2,
            })
            .from(
              `.${styles.title}`,
              {
                bottom: isTallScreen ? '0%' : '20%',
                scale: isTallScreen ? 1.25 : 1.1,
                delay: 1,
                duration: 1.5,
                ease: 'power1.inOut',
              },
              '-=0.8',
            )
            .from(
              `.${styles.table}`,
              {
                bottom:
                  -((Math.max(TABLE_IMAGE_WIDTH_PX, window.innerWidth * 0.8) / 4) * 1.5) + 150,
                duration: 2.5,
                ease: blendEases('power1.in', 'elastic.out(5, 0.7)'),
              },
              '<',
            )
            .to(
              `.${styles.table}`,
              {
                y: 20,
                duration: 2.5,
                repeat: -1,
                yoyo: true,
                ease: 'power1.inOut',
              },
              '-=1.5',
            );

          // parallax
          gsap.to(`.${styles.table}`, {
            yPercent: 50,
            scrollTrigger: {
              start: 'top top',
              end: 'bottom top',
              scrub: true,
            } satisfies ScrollTrigger.Vars,
          });
          gsap.to(`.${styles.backClouds}`, {
            yPercent: 200,
            scrollTrigger: {
              start: 'top top',
              end: 'bottom top',
              scrub: true,
            } satisfies ScrollTrigger.Vars,
          });

          // run animation on load only
          if (hasLoadedBefore) {
            tl.seek('100%');
          } else {
            setHasLoadedBefore(true);
          }
        },
      );
    },
    { scope: containerRef },
  );

  return (
    <div ref={containerRef} className={clsx(styles.container, className)} data-theme="panoramic">
      <div className={styles.topContainer}>
        <ScrollingItems
          animationDuration={400}
          animationDirection="right"
          className={styles.topCloud}
        >
          <img src={topCloud} alt="" />
        </ScrollingItems>
        <ScrollingItems
          animationDuration={400}
          animationDirection="right"
          className={styles.backClouds}
        >
          <img src={backClouds} alt="" />
        </ScrollingItems>
        <div className={styles.textContainer}>
          <h1 className={styles.title}>{title}</h1>
        </div>
        <div className={styles.tableContainer}>
          <img src={table} className={styles.table} alt="" />
        </div>
      </div>
      <ScrollingItems
        animationDuration={100}
        animationDirection="right"
        className={styles.frontClouds}
      >
        <img src={frontClouds} alt="" />
      </ScrollingItems>
    </div>
  );
};

export default HomepageHero;
